'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface CaseStudy {
  id: number;
  company: string;
  industry: string;
  campaignType: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  testimonial: string;
  author: string;
  role: string;
  image: string;
  alt: string;
  logo: string;
}

const CaseStudySpotlight = () => {
  const [selectedIndustry, setSelectedIndustry] = useState<string>('All');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('All');

  const industries = ['All', 'SaaS', 'E-commerce', 'Healthcare', 'Financial Services', 'Manufacturing'];
  const campaignTypes = ['All', 'Lead Generation', 'Email Marketing', 'Content Marketing', 'Social Media', 'Account-Based Marketing'];

  const caseStudies: CaseStudy[] = [
  {
    id: 1,
    company: 'TechFlow Solutions',
    industry: 'SaaS',
    campaignType: 'Lead Generation',
    challenge: 'Struggling with low-quality leads and a 1.2% conversion rate despite high marketing spend. Manual lead scoring was inconsistent and time-consuming.',
    solution: 'Implemented AI-powered lead scoring system with automated nurture sequences and personalized content delivery based on behavioral triggers.',
    results: [
    { metric: 'Lead Quality', value: '+340%', icon: 'UserGroupIcon' },
    { metric: 'Conversion Rate', value: '+289%', icon: 'ChartBarIcon' },
    { metric: 'Cost Per Lead', value: '-52%', icon: 'CurrencyDollarIcon' },
    { metric: 'Sales Cycle', value: '-35%', icon: 'ClockIcon' }],

    testimonial: 'Red Letter X transformed our entire marketing operation. We went from drowning in unqualified leads to having a predictable pipeline of high-intent prospects. The ROI was evident within the first quarter.',
    author: 'Sarah Chen',
    role: 'VP of Marketing',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a48b2d0-1763294125036.png",
    alt: 'Professional Asian woman with long black hair in navy blazer smiling confidently in modern office',
    logo: 'TF'
  },
  {
    id: 2,
    company: 'RetailHub Commerce',
    industry: 'E-commerce',
    campaignType: 'Email Marketing',
    challenge: 'Generic email campaigns with declining open rates and minimal personalization. Customer segmentation was basic and manual.',
    solution: 'Deployed AI-driven email personalization engine with dynamic content blocks, predictive send-time optimization, and behavioral segmentation.',
    results: [
    { metric: 'Open Rate', value: '+156%', icon: 'EnvelopeOpenIcon' },
    { metric: 'Click-Through', value: '+234%', icon: 'CursorArrowRaysIcon' },
    { metric: 'Revenue Per Email', value: '+412%', icon: 'CurrencyDollarIcon' },
    { metric: 'Unsubscribe Rate', value: '-67%', icon: 'ArrowTrendingDownIcon' }],

    testimonial: 'The level of personalization we can now achieve is remarkable. Our customers feel like we truly understand them, and the revenue impact has been transformative.',
    author: 'Michael Rodriguez',
    role: 'Director of Digital Marketing',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11ca9221a-1763300093124.png",
    alt: 'Hispanic man with short dark hair in gray suit smiling warmly in corporate setting',
    logo: 'RH'
  },
  {
    id: 3,
    company: 'HealthCare Innovations',
    industry: 'Healthcare',
    campaignType: 'Content Marketing',
    challenge: 'Complex buyer journey with multiple stakeholders. Content distribution was manual and lacked targeting precision.',
    solution: 'Created AI-powered content recommendation engine with automated distribution across channels and stakeholder-specific messaging.',
    results: [
    { metric: 'Content Engagement', value: '+278%', icon: 'DocumentTextIcon' },
    { metric: 'Lead Velocity', value: '+189%', icon: 'BoltIcon' },
    { metric: 'Pipeline Value', value: '+445%', icon: 'ChartBarIcon' },
    { metric: 'Content ROI', value: '+567%', icon: 'TrendingUpIcon' }],

    testimonial: 'Red Letter X helped us navigate the complexity of healthcare marketing with precision. Our content now reaches the right decision-makers at exactly the right time.',
    author: 'Dr. Emily Watson',
    role: 'Chief Marketing Officer',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1757e492a-1763293666438.png",
    alt: 'Professional woman with blonde hair in white medical coat smiling in healthcare facility',
    logo: 'HI'
  }];


  const filteredCaseStudies = caseStudies.filter((study) => {
    const industryMatch = selectedIndustry === 'All' || study.industry === selectedIndustry;
    const campaignMatch = selectedCampaign === 'All' || study.campaignType === selectedCampaign;
    return industryMatch && campaignMatch;
  });

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-full mb-4">
            <Icon name="StarIcon" size={20} className="text-accent" variant="solid" />
            <span className="text-sm font-semibold text-primary">Client Success Stories</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Real Results from Real Businesses
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            See how companies across industries have transformed their marketing operations and achieved measurable growth with our AI-powered solutions.
          </p>
        </div>

        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-foreground mb-2">Filter by Industry</label>
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="w-full px-4 py-3 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground">

              {industries.map((industry) =>
              <option key={industry} value={industry}>{industry}</option>
              )}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-semibold text-foreground mb-2">Filter by Campaign Type</label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="w-full px-4 py-3 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground">

              {campaignTypes.map((type) =>
              <option key={type} value={type}>{type}</option>
              )}
            </select>
          </div>
        </div>

        <div className="space-y-8">
          {filteredCaseStudies.map((study) =>
          <div key={study.id} className="bg-card rounded-2xl shadow-elevated overflow-hidden border border-border">
              <div className="grid lg:grid-cols-3 gap-8 p-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                          <span className="text-lg font-heading font-bold text-primary-foreground">{study.logo}</span>
                        </div>
                        <div>
                          <h3 className="text-2xl font-heading font-bold text-foreground">{study.company}</h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-sm text-muted-foreground">{study.industry}</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-sm text-muted-foreground">{study.campaignType}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide mb-2">Challenge</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{study.challenge}</p>
                  </div>

                  <div>
                    <h4 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide mb-2">Solution</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{study.solution}</p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {study.results.map((result, index) =>
                  <div key={index} className="bg-muted rounded-xl p-4 text-center">
                        <Icon name={result.icon as any} size={24} className="text-primary mx-auto mb-2" />
                        <p className="text-2xl font-heading font-bold text-success mb-1">{result.value}</p>
                        <p className="text-xs text-muted-foreground">{result.metric}</p>
                      </div>
                  )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-muted rounded-xl p-6">
                    <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-accent mb-4" />
                    <p className="text-sm text-foreground leading-relaxed italic mb-4">
                      "{study.testimonial}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <AppImage
                        src={study.image}
                        alt={study.alt}
                        className="w-full h-full object-cover" />

                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{study.author}</p>
                        <p className="text-xs text-muted-foreground">{study.role}</p>
                      </div>
                    </div>
                  </div>

                  <button className="w-full px-6 py-3 bg-primary text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary/90 transition-all duration-300 flex items-center justify-center space-x-2">
                    <Icon name="DocumentTextIcon" size={20} />
                    <span>Read Full Case Study</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredCaseStudies.length === 0 &&
        <div className="text-center py-12">
            <Icon name="FunnelIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">No case studies match your selected filters. Try adjusting your criteria.</p>
          </div>
        }
      </div>
    </section>);

};

export default CaseStudySpotlight;